import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import type { Case } from '../../types';
import SubTabBar from '../../components/SubTabBar';
import AssetsTab from '../../components/tabs/AssetsTab';
import NetValueSplitTab from '../../components/tabs/NetValueSplitTab';
import { useReclassifyCreditCards } from '../../hooks/useStatements';

interface Props {
  caseData: Case;
}

export default function EstateSection({ caseData }: Props) {
  const caseId = caseData.public_id;

  // Self-heal: if a credit-card statement exists with the same name as a
  // "Bank Account" asset, silently move it to liabilities. This keeps the
  // estate view in sync with the Accounts tab's account-type tags without
  // requiring the user to click the manual "Reclassify Credit Cards" button.
  const reclassifyCC = useReclassifyCreditCards(caseId);
  useEffect(() => {
    if (!caseId) return;
    reclassifyCC.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <SubTabBar
        basePath={`/cases/${caseData.public_id}/estate`}
        tabs={[
          { to: 'split', label: 'Net Value Split' },
          { to: 'assets', label: 'Assets' },
        ]}
      />
      <div className="flex-1 overflow-y-auto [scrollbar-gutter:stable] p-6">
        <Routes>
          <Route path="assets" element={<AssetsTab caseId={caseId} caseData={caseData} />} />
          <Route path="split" element={<NetValueSplitTab caseId={caseId} caseData={caseData} />} />
          {/* Absolute path — see InvestigationSection: relative `to` under a splat loops. */}
          <Route path="*" element={<Navigate to={`/cases/${caseData.public_id}/estate/split`} replace />} />
        </Routes>
      </div>
    </div>
  );
}
